import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { dataService } from '@/lib/dataService'
import { useAuth } from '@/contexts/AuthContext'

export const Register: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [activeGradeForSubject, setActiveGradeForSubject] = useState<Record<number, string>>({})
  
  // Data lists from DB
  const [departments, setDepartments] = useState<any[]>([])
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [availableSubjects, setAvailableSubjects] = useState<any[]>([])

  // Step 1: Account fields
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [selectedDept, setSelectedDept] = useState('')

  // Step 2: Subject configuration
  // Array of { tempId, code, name, max_score_pre: number, max_score_post: number, isNew: boolean, id: number | null, searchText: string }
  const [selectedSubjects, setSelectedSubjects] = useState<any[]>([
    { tempId: 1, code: '', name: '', max_score_pre: 10, max_score_post: 10, isNew: true, id: null, searchText: '' }
  ])

  // Step 3: Classroom Assignments mapping
  // Map of { [subjectTempId]: [classroomId1, classroomId2, ...] }
  const [assignedClassrooms, setAssignedClassrooms] = useState<Record<number, number[]>>({ 1: [] })

  useEffect(() => {
    // If logged in via Google and pending approval, show success screen
    if (user && user.status === 'pending_approval') {
      setRegistrationSuccess(true)
    }
    // If logged in via Google and no profile exists yet (new Google user onboarding)
    else if (user && !user.status) {
      setStep(2) // Skip Step 1
      setEmail(user.email || '')
      setUsername(user.email || '')
      
      const meta = user.user_metadata || {}
      const fullName = meta.full_name || ''
      if (fullName) {
        const parts = fullName.split(' ')
        setFirstName(parts[0] || '')
        setLastName(parts.slice(1).join(' ') || '')
      } else {
        setFirstName(meta.first_name || '')
        setLastName(meta.last_name || '')
      }
    }
  }, [user])

  useEffect(() => {
    const fetchOnboardingData = async () => {
      try {
        const [depts, classes, subs] = await Promise.all([
          dataService.getDepartments(),
          dataService.getClassrooms(),
          dataService.getSubjects()
        ])
        setDepartments(depts)
        setClassrooms(classes)
        setAvailableSubjects(subs)
        if (depts.length > 0) setSelectedDept(String(depts[0].id))
      } catch (err) {
        console.error('Error fetching register details:', err)
      }
    }
    fetchOnboardingData()
  }, [])

  // Step 2 Helpers
  const handleAddSubjectRow = () => {
    const nextTempId = selectedSubjects.length > 0 ? Math.max(...selectedSubjects.map(s => s.tempId)) + 1 : 1
    setSelectedSubjects(prev => [
      ...prev,
      { tempId: nextTempId, code: '', name: '', max_score_pre: 10, max_score_post: 10, isNew: true, id: null, searchText: '' }
    ])
    setAssignedClassrooms(prev => ({ ...prev, [nextTempId]: [] }))
  }

  const handleRemoveSubjectRow = (tempId: number) => {
    setSelectedSubjects(prev => prev.filter(s => s.tempId !== tempId))
    const updatedAssignments = { ...assignedClassrooms }
    delete updatedAssignments[tempId]
    setAssignedClassrooms(updatedAssignments)
  }

  const handleSubjectRowChange = (tempId: number, field: string, val: any) => {
    setSelectedSubjects(prev => prev.map(s => {
      if (s.tempId !== tempId) return s
      
      if (field === 'id') {
        const idVal = val === '' ? null : Number(val)
        if (idVal) {
          const matched = availableSubjects.find(sub => sub.id === idVal)
          return { 
            ...s, 
            id: idVal, 
            code: matched?.code || '', 
            name: matched?.name || '', 
            max_score_pre: matched?.max_score_pre || 10, 
            max_score_post: matched?.max_score_post || 10, 
            isNew: false 
          }
        } else {
          return { ...s, id: null, code: '', name: '', isNew: true }
        }
      }
      return { ...s, [field]: val }
    }))
  }

  // Next Step validation checks
  const handleNextStep = () => {
    setError(null)
    if (step === 1) {
      if (!email || !username || !password || !firstName || !lastName || !selectedDept) {
        setError('กรุณากรอกข้อมูลส่วนตัวให้ครบทุกช่อง')
        return
      }
      setStep(2)
    } else if (step === 2) {
      // Validate subjects
      const invalid = selectedSubjects.some(s => !s.code || !s.name || s.max_score_pre <= 0 || s.max_score_post <= 0)
      if (invalid) {
        setError('กรุณากรอกรหัสวิชา ชื่อวิชา และคะแนนเต็ม (ต้องมากกว่า 0) ให้ครบทุกช่อง')
        return
      }
      setStep(3)
    }
  }

  // Form submission (registers and links everything)
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      // 1. Process subjects list (assigning random IDs to custom new ones if not existing)
      const finalSubjects = selectedSubjects.map(sub => {
        if (!sub.id) {
          return {
            ...sub,
            id: Math.floor(Math.random() * 1000000)
          }
        }
        return sub
      })

      // 2. Prepare assignments list
      const finalAssignments: any[] = []
      finalSubjects.forEach(sub => {
        const rooms = assignedClassrooms[sub.tempId] || []
        rooms.forEach(classId => {
          finalAssignments.push({
            subject_id: sub.id,
            classroom_id: classId
          })
        })
      })

      if (finalAssignments.length === 0) {
        throw new Error('กรุณาเลือกห้องเรียนที่สอนอย่างน้อย 1 ห้อง')
      }

      // 3. Register user and setup mappings
      const profileData = {
        email,
        username: username || email,
        password: password || 'google-auth-no-password',
        first_name: firstName,
        last_name: lastName,
        department_id: Number(selectedDept)
      }

      const result = await dataService.registerTeacher(profileData, finalSubjects, finalAssignments, user?.id)
      
      if (result.success && result.profileId) {
        setRegistrationSuccess(true)
        // If logged in via Google, reload window to update AuthContext state
        if (user) {
          setTimeout(() => {
            window.location.reload()
          }, 1500)
        }
      }
    } catch (err: any) {
      const errMsg = err.message || ''
      if (errMsg.toLowerCase().includes('rate limit') || errMsg.toLowerCase().includes('rate_limit')) {
        setError(
          `จำกัดอัตราการส่งอีเมลของระบบโปรเจกต์ Supabase เต็มแล้ว (Email Rate Limit Exceeded)\n\n` +
          `กรุณาแจ้งแอดมินหรือเข้าไปปรับตั้งค่าใน Supabase Dashboard ดังนี้:\n` +
          `1. ล็อกอินเข้าใช้งาน https://supabase.com\n` +
          `2. ไปที่ Authentication -> Settings\n` +
          `3. ที่หัวข้อ "Email Provider" -> ปิดใช้งานสวิตช์ "Confirm email" (หากต้องการให้ลงทะเบียนแล้วเปิดใช้บัญชีได้ทันทีโดยไม่ต้องคลิกยืนยันลิงก์ในกล่องจดหมาย)\n` +
          `4. ที่หัวข้อ "Rate Limits" -> ปรับเพิ่มค่า "Email Rate Limit" หรือ "Signup Rate Limit" ให้มากขึ้นต่อชั่วโมง`
        )
      } else {
        setError(errMsg || 'เกิดข้อผิดพลาดในการสมัครสมาชิก')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Orbs */}
      <div className="glow-orb glow-orb-primary" />
      <div className="glow-orb glow-orb-secondary" />

      {registrationSuccess ? (
        <div className="glassmorphism" style={{ padding: '40px 32px', width: '100%', maxWidth: '500px', zIndex: 1, textAlign: 'center' }}>
          {user ? (
            <>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary)', marginBottom: '12px' }}>รอการยืนยันสิทธิ์จากแอดมิน</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '16px', lineHeight: '1.6' }}>
                บัญชี Google ของคุณเชื่อมต่อกับระบบบูรณาการวิทยาศาสตร์เทคโนโลยี SciSynapseX เรียบร้อยแล้ว
              </p>
              <div style={{ 
                background: 'rgba(99, 102, 241, 0.05)', 
                borderLeft: '4px solid var(--primary)', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                textAlign: 'left', 
                fontSize: '13px', 
                color: 'var(--text-secondary)',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                📧 <strong>บัญชีผู้ใช้:</strong> {user.email}
                <br /><br />
                ⏳ <strong>ขั้นตอนถัดไป:</strong> กรุณารอผู้ดูแลระบบ (Admin) ยืนยันและอนุมัติสิทธิ์การเข้าใช้งาน เมื่อได้รับการอนุมัติแล้ว คุณจะสามารถเข้าใช้งานระบบได้ทันทีโดยไม่ต้องตั้งค่าใหม่อีกครั้ง
              </div>
              <button 
                type="button"
                onClick={() => {
                  logout()
                  navigate('/login')
                }} 
                className="btn btn-outline"
                style={{ width: '100%', padding: '10px 20px', fontWeight: '700' }}
              >
                🚪 ออกจากระบบ / เปลี่ยนบัญชี
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--success)', marginBottom: '12px' }}>สมัครสมาชิกสำเร็จ!</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '16px', lineHeight: '1.6' }}>
                ข้อมูลการลงทะเบียนของคุณได้รับการบันทึกเข้าสู่ระบบเรียบร้อยแล้ว
              </p>
              <div style={{ 
                background: 'rgba(0, 184, 148, 0.05)', 
                borderLeft: '4px solid var(--success)', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                textAlign: 'left', 
                fontSize: '13px', 
                color: 'var(--text-secondary)',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                📧 <strong>การแจ้งเตือนอีเมล:</strong> ระบบได้ส่งอีเมลยืนยันการสมัครสมาชิกไปยัง <strong>{email}</strong> เรียบร้อยแล้ว (ท่านได้สมัครเรียบร้อยแล้ว)
                <br /><br />
                🔒 <strong>สถานะสิทธิ์การใช้งาน:</strong> บัญชีของคุณจัดอยู่ในสถานะ <strong>"รอการอนุมัติเปิดใช้งาน"</strong> โดยข้อมูลผู้ใช้งานได้ถูกส่งไปยังแผงผู้ดูแลระบบเพื่อเปิดสิทธิ์การใช้งานแล้ว
              </div>
              <button 
                type="button"
                onClick={() => navigate('/login')} 
                className="btn btn-primary"
                style={{ width: '100%', padding: '10px 20px', fontWeight: '700' }}
              >
                กลับไปยังหน้าเข้าสู่ระบบ
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="glassmorphism" style={{ padding: '40px 32px', width: '100%', maxWidth: '640px', zIndex: 1 }}>
        
        {/* Onboarding Wizard Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
            {user ? '🔗 ตั้งค่าบัญชีและข้อมูลรายวิชา (ครั้งแรก)' : '📝 ลงทะเบียนครูผู้สอนใหม่'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            {user ? 'กรุณากรอกข้อมูลเพื่อเชื่อมต่อห้องเรียนและกลุ่มสาระสำหรับเข้าใช้งานครั้งแรก' : 'ระบบบันทึกและจัดการคะแนน Pre-Test & Post-Test'}
          </p>

          {!user && (
            <>
              {/* Wizard step progress bubbles */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
                {[1, 2, 3].map(s => (
                  <div 
                    key={s} 
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: step === s ? 'var(--primary)' : step > s ? 'var(--success)' : 'var(--border)',
                      color: step >= s ? '#ffffff' : 'var(--text-secondary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '13px',
                      transition: 'background 0.3s ease'
                    }}
                  >
                    {step > s ? '✓' : s}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px', fontWeight: '500' }}>
                {step === 1 && 'ขั้นตอนที่ 1: ข้อมูลส่วนตัว'}
                {step === 2 && 'ขั้นตอนที่ 2: ตั้งค่ารายวิชาและคะแนนเต็ม'}
                {step === 3 && 'ขั้นตอนที่ 3: เลือกห้องเรียนที่สอน'}
              </div>
            </>
          )}
          
          {user && (
            <div style={{ fontSize: '14px', color: 'var(--primary)', marginTop: '16px', fontWeight: '600' }}>
              {step === 2 && '📝 ขั้นตอนที่ 1: เลือกกลุ่มสาระ & ตั้งค่าวิชาที่สอน'}
              {step === 3 && '🏫 ขั้นตอนที่ 2: เลือกห้องเรียนที่สอนในแต่ละวิชา'}
            </div>
          )}
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: 'var(--danger)',
            fontSize: '13px',
            fontWeight: '500',
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            textAlign: 'left'
          }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleRegisterSubmit}>
          
          {/* STEP 1: Personal profile registration */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="input-label" htmlFor="firstName">ชื่อจริง</label>
                  <input 
                    id="firstName"
                    type="text" 
                    className="input-field" 
                    value={firstName} 
                    onChange={(e) => setFirstName(e.target.value)} 
                    placeholder="เช่น พัชรี"
                    required
                  />
                </div>
                <div>
                  <label className="input-label" htmlFor="lastName">นามสกุล</label>
                  <input 
                    id="lastName"
                    type="text" 
                    className="input-field" 
                    value={lastName} 
                    onChange={(e) => setLastName(e.target.value)} 
                    placeholder="เช่น อักษรดี"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="input-label" htmlFor="email">อีเมลผู้ใช้งาน</label>
                <input 
                  id="email"
                  type="email" 
                  className="input-field" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="เช่น teacher@school.ac.th"
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="input-label" htmlFor="username">ชื่อผู้ใช้ (Username)</label>
                  <input 
                    id="username"
                    type="text" 
                    className="input-field" 
                    value={username} 
                    onChange={(e) => setUsername(e.target.value)} 
                    placeholder="เช่น user_patchari"
                    required
                  />
                </div>
                <div>
                  <label className="input-label" htmlFor="password">รหัสผ่าน</label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      id="password"
                      type={showPassword ? 'text' : 'password'} 
                      className="input-field" 
                      style={{ width: '100%', paddingRight: '44px' }}
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      placeholder="ขั้นต่ำ 6 ตัวอักษร"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--text-secondary)'
                      }}
                      title={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                    >
                      {showPassword ? '👁️' : '🙈'}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="input-label" htmlFor="department">กลุ่มสาระการเรียนรู้</label>
                <select 
                  id="department"
                  className="input-field" 
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  style={{ background: 'var(--bg-input)' }}
                  required
                >
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                <Link to="/login" style={{ color: 'var(--text-secondary)', fontSize: '13px', textDecoration: 'none' }}>ย้อนกลับหน้าล็อกอิน</Link>
                <button type="button" onClick={handleNextStep} className="btn btn-primary" style={{ padding: '10px 24px' }}>
                  ขั้นตอนถัดไป →
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Subject select or dynamic creation and max score settings */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', fontWeight: '700' }}>📚 รายวิชาที่คุณครูผู้รับผิดชอบการสอน</span>
                <button 
                  type="button" 
                  onClick={handleAddSubjectRow} 
                  className="btn btn-outline"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  ➕ เพิ่มวิชาอื่น
                </button>
              </div>

              {selectedSubjects.map((sub) => (
                <div 
                  key={sub.tempId} 
                  className="glassmorphism" 
                  style={{ 
                    padding: '20px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '16px', 
                    background: 'rgba(255, 255, 255, 0.4)',
                    position: 'relative'
                  }}
                >
                  {selectedSubjects.length > 1 && (
                    <button 
                      type="button" 
                      onClick={() => handleRemoveSubjectRow(sub.tempId)}
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--danger)',
                        cursor: 'pointer',
                        fontWeight: '700',
                        fontSize: '14px'
                      }}
                      title="ลบวิชานี้"
                    >
                      ✕
                    </button>
                  )}

                   <div>
                    <label className="input-label" htmlFor={`subjectSearch-${sub.tempId}`}>🔍 ค้นหารายวิชาที่มีอยู่</label>
                    <input 
                      id={`subjectSearch-${sub.tempId}`}
                      type="text" 
                      className="input-field" 
                      style={{ marginBottom: '8px' }}
                      value={sub.searchText || ''}
                      onChange={(e) => handleSubjectRowChange(sub.tempId, 'searchText', e.target.value)}
                      placeholder="พิมพ์รหัสวิชา หรือ ชื่อวิชา เพื่อค้นหา..."
                    />

                    <label className="input-label" htmlFor={`subjectSelect-${sub.tempId}`}>เลือกวิชาเรียนที่มีอยู่</label>
                    <select 
                      id={`subjectSelect-${sub.tempId}`}
                      className="input-field"
                      value={sub.id || ''}
                      onChange={(e) => handleSubjectRowChange(sub.tempId, 'id', e.target.value)}
                      style={{ background: 'var(--bg-input)' }}
                    >
                      <option value="">-- หรือพิมพ์สร้างรายวิชาใหม่ด้านล่าง --</option>
                      {availableSubjects
                        .filter(s => {
                          const query = (sub.searchText || '').toLowerCase()
                          return s.code.toLowerCase().includes(query) || s.name.toLowerCase().includes(query)
                        })
                        .map(s => (
                          <option key={s.id} value={s.id}>[{s.code}] {s.name}</option>
                        ))
                      }
                    </select>
                  </div>

                  {sub.isNew && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                      <div>
                        <label className="input-label" htmlFor={`code-${sub.tempId}`}>รหัสวิชาใหม่</label>
                        <input 
                          id={`code-${sub.tempId}`}
                          type="text" 
                          className="input-field" 
                          value={sub.code}
                          onChange={(e) => handleSubjectRowChange(sub.tempId, 'code', e.target.value)}
                          placeholder="เช่น ว21101"
                          required
                        />
                      </div>
                      <div>
                        <label className="input-label" htmlFor={`name-${sub.tempId}`}>ชื่อวิชาเรียนใหม่</label>
                        <input 
                          id={`name-${sub.tempId}`}
                          type="text" 
                          className="input-field" 
                          value={sub.name}
                          onChange={(e) => handleSubjectRowChange(sub.tempId, 'name', e.target.value)}
                          placeholder="เช่น วิทยาศาสตร์พื้นฐาน"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Subject Max Score Configuration */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                    <div>
                      <label className="input-label" htmlFor={`maxPre-${sub.tempId}`}>คะแนนเต็ม Pre-Test (ก่อนเรียน)</label>
                      <input 
                        id={`maxPre-${sub.tempId}`}
                        type="number" 
                        className="input-field" 
                        value={sub.max_score_pre}
                        onChange={(e) => handleSubjectRowChange(sub.tempId, 'max_score_pre', e.target.value)}
                        placeholder="10"
                        min="1"
                        required
                      />
                    </div>
                    <div>
                      <label className="input-label" htmlFor={`maxPost-${sub.tempId}`}>คะแนนเต็ม Post-Test (หลังเรียน)</label>
                      <input 
                        id={`maxPost-${sub.tempId}`}
                        type="number" 
                        className="input-field" 
                        value={sub.max_score_post}
                        onChange={(e) => handleSubjectRowChange(sub.tempId, 'max_score_post', e.target.value)}
                        placeholder="10"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                </div>
              ))}

              <div style={{ display: 'flex', justifyContent: user ? 'flex-end' : 'space-between', alignItems: 'center', marginTop: '16px' }}>
                {!user && (
                  <button type="button" onClick={() => setStep(1)} className="btn btn-outline" style={{ padding: '10px 24px' }}>
                    ← ย้อนกลับ
                  </button>
                )}
                <button type="button" onClick={handleNextStep} className="btn btn-primary" style={{ padding: '10px 24px' }}>
                  ขั้นตอนถัดไป →
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Assigning classrooms to each selected subject */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <span style={{ fontSize: '15px', fontWeight: '700' }}>🏫 เลือกห้องเรียนที่ทำการสอนในแต่ละวิชา</span>

              {selectedSubjects.map((sub) => {
                const subjectNameText = sub.id 
                  ? availableSubjects.find(s => s.id === sub.id)?.name 
                  : sub.name
                const subjectCodeText = sub.id 
                  ? availableSubjects.find(s => s.id === sub.id)?.code 
                  : sub.code
                const rooms = assignedClassrooms[sub.tempId] || []

                // Local helper functions inside map loop
                const getSelectedRoomsDisplay = () => {
                  if (rooms.length === 0) return 'ยังไม่ได้เลือกห้องเรียน'
                  const matched = classrooms.filter(c => rooms.includes(c.id))
                  const sorted = [...matched].sort((a, b) => {
                    if (a.grade_level !== b.grade_level) return a.grade_level - b.grade_level
                    return Number(a.room_number) - Number(b.room_number)
                  })
                  return sorted.map(c => {
                    const g = c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level
                    return `ม.${g}/${c.room_number}`
                  }).join(', ')
                }

                const handleToggleClassroomByGradeAndRoom = async (gradeLevel: number, roomNumber: string) => {
                  let targetClass = classrooms.find(c => c.grade_level === gradeLevel && c.room_number === roomNumber)
                  if (!targetClass) {
                    try {
                      targetClass = await dataService.createClassroom(gradeLevel, roomNumber)
                      const updated = await dataService.getClassrooms()
                      setClassrooms(updated)
                    } catch (err) {
                      console.error('Error creating classroom:', err)
                      return
                    }
                  }
                  const classId = targetClass.id
                  setAssignedClassrooms(prev => {
                    const current = prev[sub.tempId] || []
                    const updated = current.includes(classId)
                      ? current.filter(id => id !== classId)
                      : [...current, classId]
                    return { ...prev, [sub.tempId]: updated }
                  })
                }

                return (
                  <div key={sub.tempId} className="glassmorphism" style={{ padding: '20px', background: 'rgba(255, 255, 255, 0.4)', marginBottom: '16px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--primary)', marginBottom: '12px' }}>
                      📚 วิชา {subjectNameText} ({subjectCodeText})
                    </div>
                    
                    {/* Selected classrooms list display */}
                    <div style={{ 
                      fontSize: '12px', 
                      background: 'rgba(0, 184, 148, 0.08)', 
                      border: '1px solid rgba(0, 184, 148, 0.2)', 
                      borderRadius: '6px', 
                      padding: '8px 12px', 
                      marginBottom: '16px',
                      color: 'var(--text-primary)'
                    }}>
                      📌 <strong>ห้องเรียนที่เลือกแล้ว:</strong> {getSelectedRoomsDisplay()}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                      <div>
                        <label className="input-label" style={{ fontSize: '12px', fontWeight: '600' }}>เลือกระดับชั้นเรียน</label>
                        <select 
                          className="input-field"
                          value={activeGradeForSubject[sub.tempId] || ''}
                          onChange={(e) => setActiveGradeForSubject(prev => ({ ...prev, [sub.tempId]: e.target.value }))}
                          style={{ background: 'var(--bg-input)' }}
                        >
                          <option value="">-- เลือกระดับชั้น --</option>
                          <option value="7">ม.1</option>
                          <option value="8">ม.2</option>
                          <option value="9">ม.3</option>
                          <option value="10">ม.4</option>
                          <option value="11">ม.5</option>
                          <option value="12">ม.6</option>
                        </select>
                      </div>
                      
                      {activeGradeForSubject[sub.tempId] && (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label className="input-label" style={{ fontSize: '11px', margin: 0 }}>เลือกเลขห้องเรียน (ติ๊กถูกห้องที่สอน)</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button 
                                type="button"
                                onClick={async () => {
                                  const grade = Number(activeGradeForSubject[sub.tempId])
                                  const allRoomIds: number[] = []
                                  for (let r = 1; r <= 26; r++) {
                                    const roomStr = String(r)
                                    let cl = classrooms.find(c => c.grade_level === grade && c.room_number === roomStr)
                                    if (!cl) {
                                      cl = await dataService.createClassroom(grade, roomStr)
                                    }
                                    allRoomIds.push(cl.id)
                                  }
                                  const updatedClassrooms = await dataService.getClassrooms()
                                  setClassrooms(updatedClassrooms)
                                  
                                  setAssignedClassrooms(prev => {
                                    const current = prev[sub.tempId] || []
                                    const unique = Array.from(new Set([...current, ...allRoomIds]))
                                    return { ...prev, [sub.tempId]: unique }
                                  })
                                }}
                                style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                              >
                                [เลือกทั้งหมด]
                              </button>
                              <button 
                                type="button"
                                onClick={() => {
                                  const grade = Number(activeGradeForSubject[sub.tempId])
                                  const gradeClassroomIds = classrooms.filter(c => c.grade_level === grade).map(c => c.id)
                                  setAssignedClassrooms(prev => {
                                    const current = prev[sub.tempId] || []
                                    return { ...prev, [sub.tempId]: current.filter(id => !gradeClassroomIds.includes(id)) }
                                  })
                                }}
                                style={{ background: 'transparent', border: 'none', color: 'var(--danger)', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                              >
                                [ล้างทั้งหมด]
                              </button>
                            </div>
                          </div>
                          
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(4, 1fr)', 
                            gap: '8px', 
                            maxHeight: '130px', 
                            overflowY: 'auto', 
                            border: '1px solid var(--border)', 
                            borderRadius: '8px', 
                            padding: '10px', 
                            background: 'var(--bg-input)' 
                          }}>
                            {Array.from({ length: 26 }, (_, i) => String(i + 1)).map(roomNum => {
                              const grade = Number(activeGradeForSubject[sub.tempId])
                              const matchedClass = classrooms.find(c => c.grade_level === grade && c.room_number === roomNum)
                              const isChecked = matchedClass ? rooms.includes(matchedClass.id) : false
                              
                              return (
                                <label key={roomNum} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                                  <input 
                                    type="checkbox" 
                                    checked={isChecked}
                                    onChange={() => handleToggleClassroomByGradeAndRoom(grade, roomNum)}
                                    style={{ accentColor: 'var(--primary)' }}
                                  />
                                  ห้อง {roomNum}
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                <button type="button" onClick={() => setStep(2)} className="btn btn-outline" style={{ padding: '10px 24px' }}>
                  ← ย้อนกลับ
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn btn-primary" 
                  style={{ padding: '10px 24px', fontWeight: '700' }}
                >
                  {loading ? 'กำลังบันทึกข้อมูล...' : '✓ เสร็จสิ้นและตั้งค่าบัญชี'}
                </button>
              </div>
            </div>
          )}

        </form>
      </div>
      )}
    </div>
  )
}
export default Register
